import type { TextTexture } from '../../types/element';

export function getTextTextureSettings(texture?: TextTexture) {
  const clamp = (
    value: number | undefined,
    min: number,
    max: number,
    fallback: number
  ) =>
    typeof value === 'number' && Number.isFinite(value)
      ? Math.min(max, Math.max(min, value))
      : fallback;
  return {
    scale: clamp(texture?.scale, 1, 3, 1),
    position: {
      x: clamp(texture?.position?.x, 0, 1, 0.5),
      y: clamp(texture?.position?.y, 0, 1, 0.5),
    },
  };
}

export function hasTextTextureDimensions(texture?: TextTexture) {
  return Boolean(
    texture &&
      Number.isFinite(texture.width) &&
      Number.isFinite(texture.height) &&
      (texture.width ?? 0) > 0 &&
      (texture.height ?? 0) > 0
  );
}

// Source dimensions distinguish the continuous crop contract from legacy SVG fitting.
export function getTextTextureTransform(
  texture: TextTexture | undefined,
  width: number,
  height: number
) {
  if (
    !hasTextTextureDimensions(texture) ||
    !texture ||
    width <= 0 ||
    height <= 0
  )
    return null;
  const sourceWidth = texture.width ?? 1;
  const sourceHeight = texture.height ?? 1;
  const { scale, position } = getTextTextureSettings(texture);
  const fit =
    texture.sizing === 'contain'
      ? Math.min(width / sourceWidth, height / sourceHeight)
      : Math.max(width / sourceWidth, height / sourceHeight);
  const imageWidth =
    (texture.sizing === 'stretch' ? width : sourceWidth * fit) * scale;
  const imageHeight =
    (texture.sizing === 'stretch' ? height : sourceHeight * fit) * scale;
  return {
    width: imageWidth,
    height: imageHeight,
    x: (width - imageWidth) * position.x,
    y: (height - imageHeight) * position.y,
  };
}

// Use one paint tile spanning the glyph layout. Image geometry stays anchored
// to the saved frame; overflow outside this one source image is transparent.
export function getTextTexturePaint(
  transform: { x: number; y: number; width: number; height: number },
  origin: { x: number; y: number },
  paint: { x: number; y: number; width: number; height: number }
) {
  return {
    pattern: paint,
    image: {
      ...transform,
      x: transform.x + origin.x - paint.x,
      y: transform.y + origin.y - paint.y,
    },
  };
}
